import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const currencyRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'de' ? 'DE' : 'EN';
  const [currency, setCurrency] = useState<'EUR' | 'USD' | 'GBP'>('EUR');

  const currencySymbol: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };
  const currencyRates: Record<string, number> = { EUR: 1, USD: 1.08, GBP: 0.85 };

  // Store currency globally and notify all price components
useEffect(() => {
  (window as any).__CURRENCY__ = {
    code: currency,
    symbol: currencySymbol[currency],
    rate: currencyRates[currency],
  };

  window.dispatchEvent(new Event('currencychange'));
}, [currency]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
      if (currencyRef.current && !currencyRef.current.contains(e.target as Node)) setCurrencyOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setMobileOpen(false);
    if (location.pathname === '/' || location.pathname === '') {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const mainLinks = [
  { label: t('nav_home'), action: () => { navigate('/'); setMobileOpen(false); } },
  { label: t('nav_hurghada'), action: () => { navigate('/ausfluege-hurghada'); setMobileOpen(false); } },
  { label: t('nav_marsa_alam'), action: () => { navigate('/ausfluege-marsa-alam'); setMobileOpen(false); } },
  { label: t('nav_transfers'), action: () => { navigate('/transfers'); setMobileOpen(false); } },
  { label: t('nav_about'), action: () => { navigate('/about'); setMobileOpen(false); } },
  { label: t('nav_contact'), action: () => { navigate('/contact'); setMobileOpen(false); } },
];

  const isHome = location.pathname === '/' || location.pathname === '';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50">
      {/* === Top Header Bar === */}
      <div className={`hidden md:block transition-all duration-300 ${scrolled ? 'bg-primary-800' : 'bg-primary-900/90 backdrop-blur-sm'}`}>
        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center justify-between h-9">
            {/* Left: Contact Info */}
            <div className="flex items-center gap-6">
              <a
                href="https://wa.me/201000932585"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs text-background-100 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-sm" />
                <span>WhatsApp: +20 100 093 2585</span>
              </a>
              <a
                href="tel:+201000932585"
                className="flex items-center gap-1.5 text-xs text-background-100 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-sm" />
                <span>+20 100 093 2585</span>
              </a>
            </div>

            {/* Right: Language, Currency, Quick Booking */}
            <div className="flex items-center gap-4">
              {/* Language */}
              <div ref={langRef} className="relative">
                <button
                  onClick={() => { setLangOpen(!langOpen); setCurrencyOpen(false); }}
                  className="flex items-center gap-1 text-xs text-background-100 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap py-1"
                >
                  <i className="ri-global-line text-sm" />
                  <span>{currentLang}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
                </button>
                {langOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-primary-800 rounded-md shadow-lg border border-primary-700 py-1 min-w-[100px] z-50">
                    {['DE', 'EN'].map((l) => (
                      <button
                        key={l}
                        onClick={() => { i18n.changeLanguage(l.toLowerCase()); setLangOpen(false); }}
                        className={`w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors whitespace-nowrap ${
                          currentLang === l ? 'text-accent-400 font-medium' : 'text-background-200 hover:text-background-50 hover:bg-primary-700'
                        }`}
                      >
                        {l === 'DE' ? 'Deutsch' : 'English'}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Currency */}
              <div ref={currencyRef} className="relative">
                <button
                  onClick={() => { setCurrencyOpen(!currencyOpen); setLangOpen(false); }}
                  className="flex items-center gap-1 text-xs text-background-100 hover:text-accent-400 transition-colors cursor-pointer whitespace-nowrap py-1"
                >
                  <span>{currency} {currencySymbol[currency]}</span>
                  <i className={`ri-arrow-down-s-line text-xs transition-transform ${currencyOpen ? 'rotate-180' : ''}`} />
                </button>
                {currencyOpen && (
                  <div className="absolute top-full right-0 mt-1 bg-primary-800 rounded-md shadow-lg border border-primary-700 py-1 min-w-[100px] z-50">
                    {(['EUR', 'USD', 'GBP'] as const).map((c) => (
                      <button
                        key={c}
                       onClick={() => {
  setCurrency(c);
  setCurrencyOpen(false);

  (window as any).__CURRENCY__ = {
    code: c,
    symbol: currencySymbol[c],
    rate: currencyRates[c],
  };

  window.dispatchEvent(new CustomEvent('currencyChange'));
}}
                        className={`w-full text-left px-3 py-1.5 text-xs cursor-pointer transition-colors whitespace-nowrap ${
                          currency === c ? 'text-accent-400 font-medium' : 'text-background-200 hover:text-background-50 hover:bg-primary-700'
                        }`}
                      >
                        {c} {currencySymbol[c]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Booking */}
              <button
                onClick={() => { navigate('/contact'); }}
                className="bg-accent-500 text-primary-950 text-xs font-semibold px-4 py-1.5 rounded-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap uppercase tracking-wide"
              >
                {t('top_quick_booking')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* === Main Navigation === */}
      <div className={`transition-all duration-300 ${scrolled || !isHome ? 'bg-primary-800 shadow-lg' : 'bg-transparent'}`}>
        <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex items-center cursor-pointer flex-shrink-0">
              <img
                src="/images/yallabina-logo-white-trim.png"
                alt="Yallabina Tours Logo"
                className="h-14 md:h-16 w-auto object-contain"
              />
            </button>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-1">
              {mainLinks.map((link, i) => (
                <button
                  key={i}
                  onClick={link.action}
                  className="text-sm text-background-200 hover:text-accent-400 px-3 py-2 rounded-sm transition-colors cursor-pointer whitespace-nowrap font-medium"
                >
                  {link.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-background-50 cursor-pointer"
            >
              <i className={`${mobileOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* === Mobile Menu === */}
      {mobileOpen && (
        <div className="lg:hidden bg-primary-800 border-t border-primary-700 shadow-xl">
          {/* Mobile Logo */}
          <div className="px-4 py-4 flex items-center justify-center border-b border-primary-700/50">
            <button onClick={() => { navigate('/'); setMobileOpen(false); }} className="cursor-pointer">
              <img
                src="/images/logo-yallabina.png"
                alt="Yallabina Tours Logo"
                className="h-12 w-auto object-contain"
              />
            </button>
          </div>

          {/* Mobile contact bar */}
          <div className="px-4 py-3 flex items-center justify-between border-b border-primary-700/50">
            <a href="https://wa.me/201000932585" className="flex items-center gap-1.5 text-xs text-background-200">
              <i className="ri-whatsapp-line text-accent-400" /> WhatsApp
            </a>
            <div className="flex items-center gap-3">
              <button onClick={() => i18n.changeLanguage(i18n.language === 'de' ? 'en' : 'de')} className="text-xs text-background-200 px-2 py-1">
                {currentLang === 'DE' ? 'EN' : 'DE'}
              </button>
              <div className="flex gap-1">
                {(['EUR', 'USD', 'GBP'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    className={`text-xs px-2 py-0.5 rounded-sm ${currency === c ? 'bg-accent-500 text-primary-950 font-medium' : 'text-background-300'}`}
                  >
                    {currencySymbol[c]}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Mobile links */}
          <div className="px-4 py-3 space-y-1">
            {mainLinks.map((link, i) => (
              <button
                key={i}
                onClick={link.action}
                className="block w-full text-left px-4 py-3 text-background-200 hover:text-accent-400 hover:bg-primary-700/50 rounded-sm text-sm font-medium cursor-pointer transition-colors"
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => { navigate('/contact'); setMobileOpen(false); }}
              className="w-full mt-2 text-center px-4 py-3 bg-accent-500 text-primary-950 rounded-sm text-sm font-semibold cursor-pointer uppercase tracking-wide"
            >
              Jetzt anfragen
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}